import React, { useEffect, useState, useMemo } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { getOrder } from '../services/api';
import { format } from 'date-fns';
import { el } from 'date-fns/locale';

const STATUS_LABELS = {
  NEW: 'Νέα',
  IN_PROGRESS: 'Σε εξέλιξη',
  READY: 'Έτοιμη',
  DELIVERED: 'Παραδόθηκε',
  CANCELLED: 'Ακυρώθηκε',
};

export default function PrintOrder() {
  const { id } = useParams();
  const [params] = useSearchParams();
  // ?factory=N prints only that factory's share — what the factory queue
  // links to, so a baker gets their own ticket, not the whole order.
  const onlyFactory = params.get('factory') ? Number(params.get('factory')) : null;

  const [order, setOrder] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    getOrder(id)
      .then((r) => setOrder(r.data))
      .catch((err) => setError(err.response?.data?.error || 'Σφάλμα'));
  }, [id]);

  useEffect(() => {
    if (order) {
      const t = setTimeout(() => window.print(), 400);
      return () => clearTimeout(t);
    }
  }, [order]);

  // Items grouped by factory, honouring the ?factory filter
  const groups = useMemo(() => {
    if (!order) return [];
    const items = onlyFactory
      ? (order.items || []).filter((it) => it.factory_id === onlyFactory)
      : (order.items || []);
    const m = new Map();
    for (const it of items) {
      const key = it.factory_id || 0;
      if (!m.has(key)) m.set(key, { factory_id: key, factory_name: it.factory_name || '—', items: [] });
      const g = m.get(key);
      g.items.push(it);
    }
    return [...m.values()];
  }, [order, onlyFactory]);

  if (error) return <div style={{ padding: 40, textAlign: 'center', color: '#b91c1c' }}>{error}</div>;
  if (!order) return <div style={{ padding: 40, textAlign: 'center', color: '#6b7280' }}>Φόρτωση…</div>;

  const fmt = (iso) => format(new Date(iso), 'd MMM yyyy, HH:mm', { locale: el });
  const isPartial = onlyFactory && (order.items || []).some((it) => it.factory_id !== onlyFactory);

  return (
    <>
      <style>{`
        @media print {
          @page { margin: 12mm; }
          body { background: white !important; }
          .no-print { display: none !important; }
        }
        body { background: #f3f4f6; margin: 0; }
        .ticket {
          max-width: 600px; margin: 30px auto; background: white;
          padding: 30px 28px;
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
          color: #111; font-size: 14px; line-height: 1.45;
        }
        @media print {
          .ticket { box-shadow: none; margin: 0; padding: 0; max-width: 100%; }
        }
        .ticket h1 { margin: 0 0 4px 0; font-size: 22px; }
        .ticket .meta {
          color: #6b7280; font-size: 12px; margin-bottom: 16px;
          padding-bottom: 12px; border-bottom: 2px solid #111;
        }
        .ticket .row { display: flex; padding: 4px 0; font-size: 13px; }
        .ticket .row .label { color: #6b7280; min-width: 100px; }
        .ticket .row .value { flex: 1; font-weight: 500; }
        .ticket .notes-box {
          background: #fef3c7; border-left: 4px solid #f59e0b;
          padding: 10px 14px; margin: 14px 0; font-size: 13px;
        }
        .ticket .partial-box {
          background: #eff6ff; border-left: 4px solid #3b82f6;
          padding: 10px 14px; margin: 14px 0; font-size: 12px;
        }
        .ticket .factory-head {
          margin-top: 18px; padding: 6px 0 4px;
          border-bottom: 1px solid #111;
          font-size: 13px; font-weight: 700; text-transform: uppercase;
          letter-spacing: 0.5px;
        }
        .ticket table { width: 100%; border-collapse: collapse; margin-top: 6px; }
        .ticket th {
          text-align: left; padding: 6px 4px; border-bottom: 1px solid #9ca3af;
          font-size: 11px; text-transform: uppercase; letter-spacing: 0.5px; color: #6b7280;
        }
        .ticket td { padding: 7px 4px; border-bottom: 1px solid #e5e7eb; vertical-align: top; }
        .ticket .footer { text-align: center; margin-top: 30px; font-size: 11px; color: #9ca3af; }
        .print-bar {
          background: white; padding: 12px 20px; text-align: center;
          box-shadow: 0 1px 3px rgba(0,0,0,0.1); display: flex; justify-content: center; gap: 12px;
        }
        .print-bar button {
          padding: 8px 18px; font-size: 14px; border-radius: 6px;
          border: 1px solid #d1d5db; background: white; cursor: pointer;
        }
        .print-bar .primary { background: #3b82f6; color: white; border-color: #3b82f6; }
      `}</style>

      <div className="print-bar no-print">
        <button className="primary" onClick={() => window.print()}>🖨️ Εκτύπωση</button>
        <button onClick={() => window.close()}>Κλείσιμο</button>
      </div>

      <div className="ticket">
        <h1>Παραγγελία #{order.id}</h1>
        <div className="meta">
          {STATUS_LABELS[order.status] || order.status} · Δημιουργήθηκε: {fmt(order.created_at)}
          {order.edit_count > 0 && ` · Τροποποιήθηκε ${order.edit_count}×`}
        </div>

        <div className="row">
          <span className="label">Πελάτης:</span>
          <span className="value">{order.customer_name || '(χωρίς όνομα)'}</span>
        </div>
        {order.customer_phone && (
          <div className="row">
            <span className="label">Τηλέφωνο:</span>
            <span className="value">{order.customer_phone}</span>
          </div>
        )}
        <div className="row">
          <span className="label">Κατάστημα:</span>
          <span className="value">{order.shop_name}</span>
        </div>
        <div className="row">
          <span className="label">Παραλαβή:</span>
          <span className="value"><strong>{fmt(order.requested_for)}</strong></span>
        </div>
        {order.created_by_worker_name && (
          <div className="row">
            <span className="label">Παραγγέλθηκε από:</span>
            <span className="value">{order.created_by_worker_name}</span>
          </div>
        )}

        {isPartial && (
          <div className="partial-box">
            Αυτό το δελτίο αφορά μόνο <strong>{groups[0] ? groups[0].factory_name : ''}</strong>.
            Η παραγγελία περιλαμβάνει και προϊόντα από άλλο εργοστάσιο.
          </div>
        )}

        {order.customer_notes && (
          <div className="notes-box">
            📝 <strong>Σημείωση:</strong> {order.customer_notes}
          </div>
        )}

        {groups.map((g) => (
          <div key={g.factory_id}>
            <div className="factory-head">{g.factory_name}</div>
            <table>
              <thead>
                <tr>
                  <th style={{ width: 24 }}></th>
                  <th style={{ width: 58 }}>Κωδ.</th>
                  <th>Προϊόν</th>
                  <th style={{ textAlign: 'right', width: 70 }}>Ποσότ.</th>
                </tr>
              </thead>
              <tbody>
                {g.items.map((it) => (
                  <tr key={it.id}>
                    <td style={{ textAlign: 'center' }}>{it.status === 'READY' ? '☑' : '☐'}</td>
                    {/* Its own column rather than inline, so a picker can run
                        a finger straight down the codes */}
                    <td style={{ fontWeight: 600 }}>{it.product_code || ''}</td>
                    <td>
                      <div>{it.product_name}</div>
                      {it.notes && <div style={{ fontSize: 11, color: '#6b7280', marginTop: 2 }}>📝 {it.notes}</div>}
                    </td>
                    <td style={{ textAlign: 'right' }}>{parseFloat(it.quantity).toLocaleString('el-GR', { maximumFractionDigits: 3 })}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ))}

        {isPartial && (
          <div style={{ textAlign: 'right', fontSize: 12, color: '#6b7280', marginTop: 4 }}>
            Το δελτίο αφορά μόνο τα προϊόντα αυτού του εργοστασίου.
          </div>
        )}

        <div className="footer">Κρητικός Φούρνος · Σύστημα Παραγγελιών</div>
      </div>
    </>
  );
}
